<template>
  <div class="styled-data-table-wrapper">
    <EasyDataTable
      ref="dataTable"
      :headers="headers"
      :items="items"
      :search-value="searchValue"
      :rows-per-page="rowsPerPage"
      :theme-color="themeColor"
      v-bind="$attrs"
      :table-class-name="`styled-table ${props.tableClassName || ''}`"
      :header-item-class-name="props.headerItemClassName"
      :body-item-class-name="props.bodyItemClassName"
    >
      <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
        <slot :name="slotName" v-bind="slotProps" />
      </template>

      <template #empty-message> No Data found </template>
    </EasyDataTable>
  </div>
</template>

<script setup>
import { defineProps, ref } from "vue";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css"; // Import base styles here

// Define the props that this wrapper component accepts
// These are common props for EasyDataTable, add more as needed
// eslint-disable-next-line no-unused-vars
const props = defineProps({
  headers: {
    type: Array,
    required: true,
  },
  items: {
    type: Array,
    required: true,
  },
  searchValue: {
    type: String,
    default: "",
  },
  rowsPerPage: {
    type: Number,
    default: 10, // Default rows per page
  },
  themeColor: {
    type: String,
    default: "#34a853", // Example theme color (can be used in CSS)
  },
  tableClassName: {
    // New prop
    type: String,
    default: "",
  },
  // Add new props for class name functions
  headerItemClassName: {
    type: [Function, String], // Can be a function or a string
    default: "",
  },
  bodyItemClassName: {
    type: [Function, String], // Can be a function or a string
    default: "",
  },
  // Add other props you frequently use like:
  // sortBy, sortType, filterOptions, showIndex, etc.
});

// Expose the underlying EasyDataTable instance if needed (e.g., for calling methods)
const dataTable = ref(null);
defineExpose({ dataTable });
</script>

<style scoped>
/* Wrapper style if needed */
.styled-data-table-wrapper {
  width: 100%;
}

/* === vue3-easy-data-table Customization === */
/* Apply styles directly using :deep */
:deep(.styled-table) {
  /* Target the specific class name applied */
  /* Overall Table Appearance */
  --easy-table-border: 0px solid #e0e0e0; /* Remove outer border */
  --easy-table-row-border: 1px solid #eef0f2; /* Light border between rows */

  /* Header Styles */
  --easy-table-header-font-size: 1rem;
  --easy-table-header-height: 50px;
  --easy-table-header-font-color: #5a6474;
  --easy-table-header-background-color: #f9fafb;
  --easy-table-header-font-weight: 600;
  --easy-table-header-text-align: left;
  --easy-table-header-padding: 12px 16px;

  /* Body Styles */
  --easy-table-body-row-height: 50px;
  --easy-table-body-row-font-size: 1rem;
  --easy-table-body-row-font-color: #2c3a47;
  --easy-table-body-row-background-color: #ffffff;
  --easy-table-body-row-hover-background-color: #f9fafb;
  --easy-table-body-row-hover-font-color: #2c3a47;
  --easy-table-body-item-padding: 12px 16px;
  --easy-table-body-even-row-background-color: #ffffff; /* Override alternating bg */
  --easy-table-body-even-row-font-color: #2c3a47;

  /* Footer Styles (Pagination) */
  --easy-table-footer-background-color: #ffffff;
  --easy-table-footer-font-color: #5a6474;
  --easy-table-footer-font-size: 0.9rem;
  --easy-table-footer-padding: 0px 10px;
  --easy-table-footer-height: 50px;

  /* Optional: Style pagination buttons if needed */
  --easy-table-buttons-pagination-border: 1px solid #e0e0e0;
  --easy-table-buttons-pagination-border-radius: 4px;

  /* Use theme color if needed */
  /* --easy-table-theme-color: v-bind(themeColor); */
}

/* Override specific elements if variables aren't enough */
:deep(.vue3-easy-data-table__main.styled-table) {
  /* Target the main table */
  border-radius: 12px; /* Apply border radius */
  overflow: hidden; /* Ensure content respects the border radius */
  background-color: #fff; /* Ensure background is white */
}

/* Ensure header respects top border radius */
:deep(.vue3-easy-data-table__header th:first-child) {
  border-top-left-radius: 12px;
}
:deep(.vue3-easy-data-table__header th:last-child) {
  border-top-right-radius: 12px;
}

/* Remove default bolding from body cells if library adds it */
:deep(.vue3-easy-data-table__body td) {
  color: var(--Dark-100, #022326);

  /* Body / Reg / Small */
  font-family: "IBM Plex Sans Devanagari";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.105px;
  height: 40px;
}

:deep(.shadow) {
  box-shadow: none !important;
}
/* Remove default bolding from body cells if library adds it */
:deep(.vue3-easy-data-table__header th) {
  height: 40px;
  min-width: 50px;
  padding: 13px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;

  border-top: 1px solid var(--Border-Light, #eaecf0);
  border-bottom: 1px solid var(--Border-Light, #eaecf0);
  background: var(--Background, #fcfcfd);
}

:deep(.shadow) {
  box-shadow: none !important;
}

/* Ensure positive/negative classes (passed via slots) are styled */
/* These styles might be better placed globally or in the parent */
/* if they are specific to the data, not the table itself */
:deep(.amount-positive) {
  color: #1fb97f; /* Green */
  font-weight: 600 !important; /* Use !important only if necessary */
}
:deep(.amount-negative) {
  color: #f44336; /* Red */
  font-weight: 600 !important; /* Use !important only if necessary */
}
</style>
